import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  fetchProCellDetail,
  fetchProHeatmap,
  fetchProPhoto,
  type ProAreaDTO,
  type ProHeatmap,
  type ProResponseMap,
  type TreatmentBarDTO,
} from "@/api/adapters/patients";
import type { ProDayResponse } from "@/features/patients/pro-responses";

/**
 * Hook: the PRO item master (the questions, grouped into their 4 design areas).
 *
 * These come from the SERVER, not from a list kept in this repo: the master is owned by the admin app, so a
 * copy here would go stale the moment an administrator adds an item or changes its cadence. It is `select`ed
 * from the same one request as the grid below, which is also what makes the item ids on both sides line up.
 */
export function useProItems(patientUid: string | undefined) {
	return useQuery<ProHeatmap, Error, ProAreaDTO[]>({
		...proHeatmapQuery(patientUid),
		select: (d) => d.areas,
	});
}

/** Shared heatmap query — ONE fetch, derived below for the item master, the grid and the treatment bar. */
function proHeatmapQuery(patientUid: string | undefined) {
	return {
		queryKey: ["pro", "heatmap", patientUid] as const,
		queryFn: () => fetchProHeatmap(patientUid!),
		enabled: !!patientUid,
		staleTime: 60_000,
	};
}

/**
 * Hook: fetch PRO responses (grid) for a patient (26-week calendar).
 */
export function useProResponses(patientUid: string | undefined) {
	return useQuery<ProHeatmap, Error, ProResponseMap>({
		...proHeatmapQuery(patientUid),
		select: (d) => d.grid,
	});
}

/**
 * Hook: the treatment-window bar (discharge → maturity + current week). Same underlying
 * query as useProResponses, so no extra network request.
 */
export function useProTreatmentBar(patientUid: string | undefined) {
	return useQuery<ProHeatmap, Error, TreatmentBarDTO | null>({
		...proHeatmapQuery(patientUid),
		select: (d) => d.treatmentBar,
	});
}

/**
 * Convenience: every PRO item as one flat list, in the master's own display order.
 */
export function useFlatProItems(patientUid: string | undefined) {
	const { data: areas } = useProItems(patientUid);
	return useMemo(() => areas?.flatMap((a) => a.items) ?? [], [areas]);
}

/**
 * Hook: the day-by-day detail behind ONE heatmap square — the cell-click popover.
 *
 * Fetched only while a cell is actually open (`enabled`), and never as part of the grid: the grid is 18 items x 26
 * weeks, so loading every day of every square up front would fetch hundreds of answers to draw a panel that shows
 * one of them. Each (item, week) is cached under its own key, so re-opening a square a clinician has already looked
 * at costs nothing.
 */
export function useProCellDetail(
	patientUid: string | undefined,
	itemId: string | undefined,
	weekIndex: number | undefined,
) {
	const open = !!patientUid && !!itemId && weekIndex !== undefined;
	return useQuery<ProDayResponse[], Error>({
		queryKey: ["pro", "cell", patientUid, itemId, weekIndex] as const,
		queryFn: () => fetchProCellDetail(patientUid!, itemId!, weekIndex!),
		enabled: open,
		staleTime: 60_000,
	});
}

/**
 * Hook: one PRO answer photo, fetched through the authenticated `httpClient` and resolved to a base64
 * `data:` URI. The server's photo URL is a care-team-gated proxy endpoint — a plain `<img src>` can never
 * attach the bearer token a browser image load needs, so this hook is the only correct way to load one.
 * Cached by URL, so re-opening a popover a clinician already viewed costs no extra fetch.
 */
export function useProPhoto(proxyUrl: string | undefined) {
	return useQuery<string, Error>({
		queryKey: ["pro", "photo", proxyUrl] as const,
		queryFn: () => fetchProPhoto(proxyUrl!),
		enabled: !!proxyUrl,
		staleTime: 60_000,
	});
}
